import { FC, ReactElement, useCallback, useEffect, useMemo, useState } from 'react';

import {
	AzureDevopsTicketCreator,
	AzureTicketDraft,
	BUTTON_TYPES,
	EvButton,
	EvLink
} from '@evinced-private/ui-common';

import { useIsJiraWriteBackEnabled } from 'src/common/hooks/useIsJiraWriteBackEnabled';
import { JiraApi } from 'src/common/services/api/JiraApi';
import { getJiraAccessToken } from 'src/common/services/api/LocalStorageApi';
import { buildAzureTicketCreatorConfig } from 'src/common/services/azureTicketCreatorConfig';

import { useConfiguration } from '../../providers/configurationProvider/ConfigurationProvider';
import { formatIssueForExport } from '../jira-issue-creator/helpers/jira-export-helper';

import { buildAzureTicketDraft } from './helpers/azure-export-helper';
import { JiraCloudModule } from './JiraCloudModule';
import { ITicketsSystems } from './TicketsSystems.types';
import { ADD_TO_TICKET_SYSTEM_BTN_ID, JIRA_DESCRIPTION_PLATFORM } from './TicketSystems.constants';

export const TicketsSystems: FC<ITicketsSystems> = ({
	issue,
	type = JIRA_DESCRIPTION_PLATFORM.WEB,
	onTicketCreated
}) => {
	const isJiraWriteBackEnabled = useIsJiraWriteBackEnabled();
	const { configuration } = useConfiguration();
	const enableAzureIntegration = configuration?.PLATFORM?.CONFIG?.ENABLE_AZURE_INTEGRATION ?? false;
	const enableAzureBypassRule = configuration?.PLATFORM?.CONFIG?.ENABLE_AZURE_BYPASS_RULE ?? false;
	const [isJiraConnected, setJiraConnected] = useState(false);
	const [issueToExport, setIssueToExport] = useState(null);
	const [isJiraModalOpened, setJiraModalOpened] = useState(false);
	const issueNote = typeof issue?.description === 'string' ? issue.description : undefined;

	const exportToTicketsSystem = useCallback(
		(setModalOpened: (flag: boolean) => void): void => {
			setIssueToExport(formatIssueForExport(issue, issueNote, type));
			setModalOpened(true);
		},
		[issue, issueNote, type]
	);

	const checkIfJiraConnected = useCallback((): void => {
		const jiraToken = getJiraAccessToken();
		setJiraConnected(!!jiraToken);
	}, []);

	useEffect(() => {
		checkIfJiraConnected();
		window.addEventListener('storage', checkIfJiraConnected);

		return () => {
			window.removeEventListener('storage', checkIfJiraConnected);
		};
	}, [checkIfJiraConnected]);

	const renderAddToButton = useCallback(
		(
			ticketSystem: string,
			isConnected: boolean,
			connectUrl: string,
			setModalOpened: (flag: boolean) => void
		) => {
			const title = `Add to ${ticketSystem}`;

			if (isConnected) {
				return (
					<EvButton
						type={BUTTON_TYPES.PRIMARY}
						onClick={() => exportToTicketsSystem(setModalOpened)}
					>
						{title}
					</EvButton>
				);
			}
			return (
				<EvLink url={connectUrl} linkText={`Connect to ${ticketSystem}`} openInNewTab={true} />
			);
		},
		[exportToTicketsSystem]
	);

	const addToJiraButton = useMemo(() => {
		return renderAddToButton('Jira', isJiraConnected, JiraApi.getJiraAuthURL(), setJiraModalOpened);
	}, [renderAddToButton, isJiraConnected]);

	// Per-tenant Azure OAuth scope from central config overrides the env default (falls back to it,
	// then to the module's Azure DevOps default). Memoized so the module's provider resolves once.
	const azureConfig = useMemo(
		() => buildAzureTicketCreatorConfig(configuration?.PLATFORM?.CONFIG?.AZURE_SCOPES),
		[configuration?.PLATFORM?.CONFIG?.AZURE_SCOPES]
	);

	// Map the app's Report onto the azure-ticket-creator module's host-agnostic input contract.
	const azureTicket = useMemo<AzureTicketDraft>(
		() => buildAzureTicketDraft(issue, issueNote),
		[issue, issueNote]
	);

	const renderJiraModal = (): ReactElement => (
		<JiraCloudModule
			issueToExport={issueToExport}
			setJiraModalOpened={setJiraModalOpened}
			isJiraModalOpened={isJiraModalOpened}
			onTicketCreated={isJiraWriteBackEnabled ? onTicketCreated : undefined}
		/>
	);

	return (
		<>
			{enableAzureIntegration ? (
				<AzureDevopsTicketCreator
					config={azureConfig}
					ticket={azureTicket}
					bypassRules={enableAzureBypassRule}
					dataAutomationId={ADD_TO_TICKET_SYSTEM_BTN_ID}
				/>
			) : (
				addToJiraButton
			)}
			{issueToExport && isJiraModalOpened && renderJiraModal()}
		</>
	);
};
